// /assets/ts/main.ts

import { initSessionBroadcast } from "./utils/broadcast";
import { initLoaderService } from "./utils/loader";
import { initToastMessageService, toast } from "./utils/toast-message";

// --- Classes ---
export class ScwError extends Error {
  statusCode: number | null;
  data?: Record<any, any>;

  constructor(
    message: string,
    statusCode: number | null = null,
    data?: Record<any, any>,
  ) {
    super(message);
    this.statusCode = statusCode;
    this.data = data;
  }
}

// --- Const & DOM Cache ---
export const pErr = new Set(["scw_invalid_session", "scw_expired_session"]);

export const mainDynamicContent = document.getElementById(
  "scw-main-dynamic-content",
) as HTMLDivElement;
export const dynamicContainer = document.getElementById(
  "dynamic-container",
) as HTMLDivElement;
export const dynamicContainerHeader = document.getElementById(
  "template_dynamicContainerHeader",
) as HTMLTemplateElement;
export const errorLabelFragment = document.getElementById(
  "template_errorLabel",
) as HTMLTemplateElement;
export const sidePanel = document.getElementById("side-panel") as HTMLElement;

initToastMessageService();
initLoaderService();

// Connection to a broadcast channel
initSessionBroadcast();

// --- Utils ---
interface ScwFetchOptions extends RequestInit {
  notificationContainer?: HTMLDivElement | null;
  body?: any;
  notify?: boolean;
}

export async function scwFetch<T = any>(
  url: string,
  {
    method = "GET",
    credentials = "include",
    notificationContainer,
    notify = true,
    ...options
  }: ScwFetchOptions = {},
): Promise<T> {
  try {
    const headers = new Headers(options?.headers);
    headers.set("Content-Type", "application/json");

    const response = await fetch(url, {
      ...(options ?? {}),
      ...(options?.body ? { body: JSON.stringify(options.body) } : {}),
      method,
      headers,
    });

    const data = response?.body ? await response.json().catch(() => ({})) : {};

    if (!response.ok) {
      throw new ScwError(
        data.error || "Error: " + response.status + " " + response.statusText,
        response.status,
        data,
      );
    }

    return data;
  } catch (error: any) {
    if (notify) handleError(error, notificationContainer);
    throw error;
  }
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
) {
  let timeout: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export function handleError(
  error: Error | ScwError,
  notificationContainer?: HTMLDivElement | null,
) {
  if (pErr.has(error.message)) {
    showErrorPage(error);
    return;
  }

  let errorMessage = error?.message;

  if (error && error instanceof ScwError && error.data?.errConfig?.Message) {
    const meta = error.data?.meta;
    const message = interpolateBasicString(error.data.errConfig.Message, meta);
    const detail = interpolateBasicString(error.data.errConfig.Detail, meta);

    errorMessage = message + (detail ? `: ${detail}` : "");
  }

  toast.error(
    errorMessage,
    notificationContainer &&
      notificationContainer instanceof HTMLElement &&
      notificationContainer?.isConnected
      ? notificationContainer
      : undefined,
  );
}

export function showErrorPage(error: Error | ScwError) {
  clearDynamicContainer();
  toggleSidePanel(true);

  setContainerHeader({
    title: "Error",
    subTitle: error.message,
  });

  const templates = {
    errorPage: document.getElementById(
      "template_errorPage",
    ) as HTMLTemplateElement,
  };

  const containerFragment = templates.errorPage?.content?.cloneNode(
    true,
  ) as DocumentFragment;
  const containerElement = containerFragment.querySelector(
    ".scw-template-container",
  ) as HTMLElement;
  const errorPageMessage = containerElement.querySelector(
    "span#errorPageMessage",
  ) as HTMLSpanElement;

  const scwError =
    error instanceof ScwError ? error : new ScwError(error.message);

  const message = scwError?.data?.errConfig?.Message ?? scwError?.message;
  const detail = scwError?.data?.errConfig?.Detail;

  errorPageMessage.innerText = interpolateBasicString(
    message,
    scwError?.data?.meta,
  );

  const errorPageDetail = containerElement.querySelector(
    "span#errorPageDetail",
  ) as HTMLSpanElement;

  errorPageDetail.innerText = interpolateBasicString(
    detail,
    scwError?.data?.meta,
  );

  const errorPageCode = containerElement.querySelector(
    "span#errorPageCode",
  ) as HTMLSpanElement;

  if (scwError.data?.errConfig?.Message && scwError.message) {
    errorPageCode.innerText = scwError.message;
  } else {
    errorPageCode.remove();
  }

  const backToLoginButton = containerElement.querySelector(
    'button[data-back-to-login="true"]',
  ) as HTMLButtonElement;

  if (scwError.data?.meta?.env) {
    backToLoginButton.addEventListener("click", async () => {
      const backUrl = new URL(
        "/view/auth/" + scwError.data?.meta?.env,
        window.location.origin,
      );
      const redirect_uri =
        scwError.data?.meta?.redirectUrl ?? scwError.data?.meta?.mainUrl;
      if (redirect_uri) {
        backUrl.searchParams.set("redirect_uri", redirect_uri);
      }
      window.location.replace(backUrl);
    });
  } else {
    backToLoginButton.remove();
  }

  dynamicContainer.append(containerFragment);
}

export function interpolateBasicString(
  template: string,
  data: Record<string, unknown> = {},
): string {
  if (!template) return "";

  // Unresolved placeholders are dropped rather than printed: a message like
  // "Access Denied: {{reason}}" is never something we want to show a user.
  return template
    .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key) => {
      const value = key.split(".").reduce((obj: any, prop: string) => {
        return obj && obj[prop] !== undefined ? obj[prop] : undefined;
      }, data);

      return value !== undefined && value !== null ? String(value) : "";
    })
    .replace(/\s{2,}/g, " ")
    .trim();
}

export const clearContainerElement = (
  containerElement?: HTMLElement | null,
) => {
  if (
    containerElement &&
    containerElement instanceof HTMLElement &&
    containerElement.isConnected
  ) {
    containerElement.replaceChildren();
    (containerElement.childNodes ?? []).forEach((child) =>
      containerElement.removeChild(child),
    );
  }
};

export const clearDynamicContainer = () =>
  clearContainerElement(dynamicContainer);

export const resetContainers = () => {
  sidePanel.classList.remove("scw-hidden");
  mainDynamicContent.classList.remove("scw-transparent");
};

export const toggleSidePanel = (shouldHide: boolean) => {
  sidePanel.classList.toggle("scw-hidden", shouldHide);
};

export const toggleDynamicContainerMode = (shouldBeTransparent?: boolean) => {
  mainDynamicContent.classList.toggle("scw-transparent", !!shouldBeTransparent);
};

interface HeaderConfig {
  title: string;
  subTitle: string;
  backButtonAction?: (event: MouseEvent) => Promise<void> | void;
}

export const setContainerHeader = (config: HeaderConfig) => {
  const header = dynamicContainerHeader?.content.cloneNode(
    true,
  ) as DocumentFragment;

  const title = header?.firstElementChild?.querySelector(
    ".scw-header-title",
  ) as HTMLElement;
  title.textContent = config?.title;
  const subTitle = header?.firstElementChild?.querySelector(
    ".scw-header-subtitle",
  ) as HTMLElement;
  subTitle.textContent = config?.subTitle;

  const backButton = header?.firstElementChild?.querySelector(
    ".scw-back-button",
  ) as HTMLButtonElement;
  if (
    config?.backButtonAction &&
    typeof config?.backButtonAction === "function"
  ) {
    backButton?.addEventListener("click", async (event) => {
      event.preventDefault();
      await config?.backButtonAction!(event);
    });
  } else {
    backButton.remove();
  }

  dynamicContainer.append(header);
};

// Reads the server-rendered JSON island. Returns a fresh object each call,
// so callers can safely treat the result as their own mutable state copy.
export const getInitialData = <T>(): T => {
  const el = document.getElementById("initial-data");
  if (!el?.textContent) {
    throw new Error("🚨 Critical Error: Initial page data is missing.");
  }
  return JSON.parse(el.textContent) as T;
};

window.addEventListener("unhandledrejection", (e) => {
  e.preventDefault();
  if (e.reason instanceof ScwError && pErr.has(e.reason.message)) {
    showErrorPage(e.reason);
    return;
  }
  toast.error("Something went wrong. Please try again.");
});

window.addEventListener("error", () => {
  toast.error("Something went wrong. Please try again.");
});
